import { useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { PACKET_CONTENT_WIDTH_PX } from '@/components/chefs/print/packet/chrome'
import { PacketMeasure } from '@/components/chefs/print/packet/packet-document'
import { layoutPacket, type PacketHeights, type PacketLayout } from '@/lib/chefs/packet/layout'
import { packetModelFor } from '@/lib/chefs/packet/model'
import type { PacketTarget } from '@/lib/chefs/packet/target'

/** A measured packet: its fixed pages, and the model signature they belong to. */
export interface PacketPlan {
  signature: string
  layout: PacketLayout
  totalPages: number
  dayCount: number
}

/** Every `[attr]` element's height by its data value. */
function readHeights(root: HTMLElement, attr: string, key: string): Map<string, number> {
  const heights = new Map<string, number>()
  root.querySelectorAll<HTMLElement>(`[${attr}]`).forEach((node) => {
    const id = node.dataset[key]
    if (id) {
      heights.set(id, node.getBoundingClientRect().height)
    }
  })
  return heights
}

/**
 * Renders the packet's sections off-screen at the print content width (a
 * probe page per section for its body height, every unit in a stream for its
 * own height), lays the packet out on fixed pages and reports the plan.
 * Re-measures whenever the model's signature changes, which it does whenever
 * a printed word does. Kept out of every print by `print-screen-only`.
 */
export function PacketMeasureHost({
  target,
  onMeasured,
}: {
  target: PacketTarget | null
  onMeasured: (plan: PacketPlan) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const model = target ? packetModelFor(target) : null
  const signature = model?.signature ?? null

  useLayoutEffect(() => {
    if (!model || !containerRef.current) {
      return
    }
    let cancelled = false
    const measure = () => {
      const el = containerRef.current
      if (cancelled || !el) {
        return
      }
      const heights: PacketHeights = {
        body: readHeights(el, 'data-packet-body', 'packetBody'),
        unit: readHeights(el, 'data-packet-unit', 'packetUnit'),
        compact: readHeights(el, 'data-packet-unit-compact', 'packetUnitCompact'),
      }
      const layout = layoutPacket(model, heights)
      onMeasured({ signature: model.signature, layout, totalPages: layout.totalPages, dayCount: model.dayCount })
    }
    // Font metrics move line heights, so measure once the fonts are in.
    const raf = requestAnimationFrame(() => {
      if (typeof document !== 'undefined' && document.fonts && document.fonts.status !== 'loaded') {
        document.fonts.ready.then(() => requestAnimationFrame(measure))
      } else {
        measure()
      }
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
    // Re-run only when what prints changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  if (!model) {
    return null
  }

  return createPortal(
    <div
      ref={containerRef}
      aria-hidden
      className="print-screen-only"
      style={{
        position: 'fixed',
        left: -100000,
        top: 0,
        width: PACKET_CONTENT_WIDTH_PX,
        visibility: 'hidden',
        pointerEvents: 'none',
      }}>
      <PacketMeasure model={model} preliminaryLocksAt={target?.preliminary?.locksAtLabel ?? null} />
    </div>,
    document.body,
  )
}
